import { useEffect, useRef, useState } from "react";

// 리더스북 쪽 그림 위의 답 칸(지인에듀 pages[].fields, 2026-10-17). 아이는 쪽 위에 바로 쓰고, 채점이
// 끝나면 같은 자리에 빨간 펜 첨삭이 얹힌다(○ · 빗금 · △, 틀린 곳에 줄 긋고 고친 말, 빠진 곳에 ∧).
//
// 좌표는 쪽 그림 폭 · 높이에 대한 비율(0~1)이라 % 로 그대로 놓는다. 글자 크기만 쪽이 그려진 실제
// 크기에서 잰다. 칸이 너무 작게 그려지는 화면(폰)에서는 칸을 누르면 아래에서 큰 입력창이 올라온다.

// 쪽이 작게 그려져도(작은 창 · 폰) 쓰고 읽을 수 있는 글자 크기. 칸이 이보다 작으면 칸을 키워 그 자리에 바로 쓴다.
const MIN_WRITING_FONT_SIZE = 15;
const MIN_READING_FONT_SIZE = 12;
const LONG_FIX_LENGTH = 18;

const percentBox = (box) => ({
  left: `${box.x * 100}%`,
  top: `${box.y * 100}%`,
  width: `${box.w * 100}%`,
  height: `${box.h * 100}%`,
});

// 손으로 그린 듯한 표시. viewBox 40x40 안에서 그린다.
const MARK_PATH = {
  ok: "M20 4 C31 3 37 11 36 21 C35 31 27 37 18 36 C9 35 3 27 4 18 C5 10 12 5 22 5",
  no: "M8 34 C16 24 24 14 34 4",
  half: "M20 4 L36 34 L4 34 Z",
};

export const VerdictMark = ({ verdict, point, size }) => {
  if (!point || !MARK_PATH[verdict]) return null;
  return (
    <svg
      className="page-field__mark"
      viewBox="0 0 40 40"
      style={{ left: `${point.x * 100}%`, top: `${point.y * 100}%`, width: size, height: size }}
    >
      <path d={MARK_PATH[verdict]} />
    </svg>
  );
};

// 채점된 글: 그대로 둔 조각, 줄 그은 조각(위에 고친 말), 빠진 자리(∧ 와 넣을 말)
export const CorrectedText = ({ segmentList, fallbackText }) => {
  if (!segmentList || !segmentList.length) return <>{fallbackText}</>;
  return (
    <>
      {segmentList.map((segment, index) => {
        if (segment.kind === "wrong" && String(segment.fix || "").length > LONG_FIX_LENGTH * 2) {
          // 문장을 통째로 고친 것은 줄 위에 얹으면 칸을 넘친다. 그은 줄 아래 따로 쓴다.
          return (
            <span key={index}>
              <span className="page-field__wrong page-field__wrong--whole">{segment.text}</span>
              <span className="page-field__fix-block">{segment.fix}</span>
            </span>
          );
        }
        if (segment.kind === "wrong") {
          return (
            <span className="page-field__wrong" key={index}>
              {segment.text}
              <span className="page-field__fix">{segment.fix}</span>
            </span>
          );
        }
        if (segment.kind === "insert") {
          // 빠진 말이 길면(뒷문장 통째로 등) 줄 위에 다 쓰면 윗줄을 덮는다. 작게 줄 바꿔 쓴다.
          const isLong = String(segment.fix || "").length > LONG_FIX_LENGTH;
          return (
            <span className="page-field__insert" key={index}>
              <span className="page-field__caret">∧</span>
              <span className={`page-field__fix${isLong ? " page-field__fix--long" : ""}`}>{segment.fix}</span>
            </span>
          );
        }
        return <span key={index}>{segment.text}</span>;
      })}
    </>
  );
};

const PageFieldLayer = ({ fieldList, responses, results, onChange, isEditable, noteNumberById }) => {
  const layerRef = useRef(null);
  const [pageSize, setPageSize] = useState({ width: 0, height: 0 });
  const [pickedLeft, setPickedLeft] = useState(null);

  useEffect(() => {
    if (!layerRef.current || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(([entry]) =>
      setPageSize({ width: entry.contentRect.width, height: entry.contentRect.height })
    );
    observer.observe(layerRef.current);
    return () => observer.disconnect();
  }, []);

  const markSize = Math.max(18, pageSize.width * 0.042);
  const fontSizeOf = (box) => Math.min(box.h * pageSize.height * 0.95, pageSize.width * 0.032);
  // 칸보다 긴 글은 칸 안에 들도록 글자를 줄인다(손글씨 글꼴 한 글자 폭을 0.55em 으로 어림)
  const fittedFontSizeOf = (fontSize, widthPixel, text) => {
    const length = String(text || "").length;
    if (!length) return fontSize;
    return Math.max(MIN_READING_FONT_SIZE - 1, Math.min(fontSize, widthPixel / (length * 0.55)));
  };

  const renderTextField = (field) => {
    const value = responses[field.id] ?? "";
    const result = results[field.id];
    const fontSize = fontSizeOf(field.box);
    const noteNumber = noteNumberById[field.id];

    if (result || !isEditable) {
      return (
        <div
          key={field.id}
          className={`page-field__hand${field.type === "textarea" ? " page-field__hand--multi" : ""}${
            value ? " page-field__hand--covered" : ""
          }`}
          style={{
            ...percentBox(field.box),
            fontSize:
              field.type === "textarea"
                ? Math.max(fontSize, MIN_READING_FONT_SIZE)
                : fittedFontSizeOf(Math.max(fontSize, MIN_READING_FONT_SIZE), field.box.w * pageSize.width, value),
          }}
        >
          {/* 칸은 flex 라 조각을 바로 넣으면 조각 사이 띄어쓰기가 사라진다. 한 줄 글로 감싼다. */}
          <span className="page-field__line">
            {result ? <CorrectedText segmentList={result.segments} fallbackText={value} /> : value}
            {noteNumber && <span className="page-field__note">{noteNumber}</span>}
          </span>
        </div>
      );
    }

    // 칸이 작게 그려지면 그 자리에서 칸을 키워 바로 쓴다(따로 입력창을 띄우면 쪽이 가려져 무엇을 쓰는지 안 보인다).
    // 글자를 읽을 만한 크기로 올리고, 칸은 밑줄 자리에 맞춰 위로 · 오른쪽으로 넓힌다.
    const isSmall = fontSize < MIN_WRITING_FONT_SIZE && field.type !== "textarea";
    let boxStyle = percentBox(field.box);
    if (isSmall) {
      const writingFontSize = MIN_WRITING_FONT_SIZE;
      const height = writingFontSize * 1.5;
      const left = field.box.x * pageSize.width;
      const width = Math.min(
        Math.max(field.box.w * pageSize.width, field.type === "number_box" ? height : writingFontSize * 7),
        pageSize.width - left
      );
      boxStyle = {
        left: `${left}px`,
        top: `${(field.box.y + field.box.h) * pageSize.height - height + 2}px`,
        width: `${width}px`,
        height: `${height}px`,
      };
    }
    const commonProps = {
      className: `page-field__input${field.type === "number_box" ? " page-field__input--center" : ""}${
        isSmall ? " page-field__input--lifted" : ""
      }${value ? " page-field__input--filled" : ""}`,
      style: {
        ...boxStyle,
        fontSize: isSmall
          ? MIN_WRITING_FONT_SIZE
          : field.type === "textarea"
            ? fontSize
            : fittedFontSizeOf(fontSize, field.box.w * pageSize.width, value),
      },
      value,
      spellCheck: false,
      autoCapitalize: "off",
      autoComplete: "off",
      onChange: (event) => onChange(field.id, event.target.value),
    };
    if (field.type === "textarea") return <textarea key={field.id} {...commonProps} />;
    return (
      <input
        key={field.id}
        {...commonProps}
        inputMode={field.type === "number_box" ? "numeric" : "text"}
        maxLength={field.type === "number_box" ? 2 : 300}
      />
    );
  };

  const renderChoiceField = (field) => {
    const picked = responses[field.id];
    const result = results[field.id];
    return (field.options || []).map((option, optionIndex) => {
      const isPicked = picked !== undefined && picked !== null && String(picked) === String(option.value);
      const isAnswer = result && result.verdict === "no" && String(field.answer) === String(option.value);
      return (
        <button
          type="button"
          key={`${field.id}-${optionIndex}`}
          className={`page-field__option${isPicked ? " is-picked" : ""}${isAnswer ? " is-answer" : ""}`}
          style={percentBox(option.box)}
          disabled={!isEditable || Boolean(result)}
          onClick={() => onChange(field.id, option.value)}
          aria-label={String(option.value)}
        />
      );
    });
  };

  const renderMatchField = (field) => {
    const pairList = Array.isArray(responses[field.id]) ? responses[field.id] : [];
    const result = results[field.id];
    const pointOf = (list, value) => list.find((one) => String(one.value) === String(value));
    const toggleLeft = (value) => {
      if (!isEditable || result) return;
      const existing = pairList.find((pair) => String(pair[0]) === String(value));
      if (existing) {
        onChange(field.id, pairList.filter((pair) => pair !== existing));
        return;
      }
      setPickedLeft(value);
    };
    const pickRight = (value) => {
      if (!isEditable || result || pickedLeft === null) return;
      const nextList = pairList.filter((pair) => String(pair[0]) !== String(pickedLeft) && String(pair[1]) !== String(value));
      onChange(field.id, [...nextList, [pickedLeft, value]]);
      setPickedLeft(null);
    };
    return (
      <div key={field.id} className="page-field__match">
        <svg viewBox="0 0 1000 1000" preserveAspectRatio="none">
          {pairList.map(([leftValue, rightValue]) => {
            const left = pointOf(field.left || [], leftValue);
            const right = pointOf(field.right || [], rightValue);
            if (!left || !right) return null;
            const isWrong = result && String(leftValue) !== String(rightValue);
            return (
              <line
                key={`${leftValue}-${rightValue}`}
                x1={left.x * 1000}
                y1={left.y * 1000}
                x2={right.x * 1000}
                y2={right.y * 1000}
                className={isWrong ? "is-wrong" : ""}
              />
            );
          })}
        </svg>
        {(field.left || []).map((point) => (
          <button
            type="button"
            key={`left-${point.value}`}
            className={`page-field__dot${String(pickedLeft) === String(point.value) ? " is-picked" : ""}`}
            style={{ left: `${point.x * 100}%`, top: `${point.y * 100}%` }}
            onClick={() => toggleLeft(point.value)}
            aria-label={String(point.value)}
          />
        ))}
        {(field.right || []).map((point) => (
          <button
            type="button"
            key={`right-${point.value}`}
            className="page-field__dot"
            style={{ left: `${point.x * 100}%`, top: `${point.y * 100}%` }}
            onClick={() => pickRight(point.value)}
            aria-label={String(point.value)}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="page-field" ref={layerRef}>
      {pageSize.width > 0 &&
        fieldList.map((field) => {
          if (field.type === "choice") return renderChoiceField(field);
          if (field.type === "match") return renderMatchField(field);
          return renderTextField(field);
        })}
      {pageSize.width > 0 &&
        fieldList.map((field) =>
          results[field.id] ? (
            <VerdictMark key={`mark-${field.id}`} verdict={results[field.id].verdict} point={field.number} size={markSize} />
          ) : null
        )}
    </div>
  );
};

export default PageFieldLayer;
